import { useEffect, useRef, useState } from "react";

interface GoldenHintProps {
  isTyping: boolean;
  mistCount?: number; // ミス回数：増えるほどヒントが霧に包まれる
}

const HINT_TEXT = "既に、知っているはずだ。";

export default function GoldenHint({ isTyping, mistCount = 0 }: GoldenHintProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const [visibleChars, setVisibleChars] = useState(0);
  const [glowPhase, setGlowPhase] = useState<"enter" | "pulse" | "dim">("enter");

  // 霧の強度：ミス1回ごとに増加、最大0.9
  const mistFog = Math.min(mistCount * 0.18, 0.9);
  // ヒントの不透明度：ミスが増えるほど薄くなる
  const hintOpacity = Math.max(0.08, 1 - mistCount * 0.22);
  // テキストのぼかし：ミスが増えるほど霞む
  const textBlur = Math.min(mistCount * 1.2, 8);

  // 文字を一つずつ浮かび上がらせる
  useEffect(() => {
    if (visibleChars >= HINT_TEXT.length) return;
    const delay = visibleChars === 0 ? 1200 : 90 + Math.random() * 60;
    const t = setTimeout(() => {
      setVisibleChars((v) => v + 1);
    }, delay);
    return () => clearTimeout(t);
  }, [visibleChars]);

  // グローフェーズ管理
  useEffect(() => {
    const t1 = setTimeout(() => setGlowPhase("pulse"), 2400);
    return () => clearTimeout(t1);
  }, []);

  // 入力中は dim
  useEffect(() => {
    if (isTyping) setGlowPhase("dim");
    else if (glowPhase === "dim") setGlowPhase("pulse");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTyping]);

  // Canvas：入力欄周辺の金の粒子＋焦りの霧
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    interface Mote {
      x: number; y: number;
      vx: number; vy: number;
      life: number; maxLife: number;
      size: number; opacity: number;
      hue: number;
    }

    interface FogMote {
      x: number; y: number;
      vx: number; vy: number;
      size: number;
      opacity: number;
      phase: number;
    }

    const motes: Mote[] = [];
    const fogMotes: FogMote[] = Array.from({ length: 20 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      size: 30 + Math.random() * 60,
      opacity: Math.random() * 0.3,
      phase: Math.random() * Math.PI * 2,
    }));

    const spawnMote = () => {
      const side = Math.random() < 0.5 ? -1 : 1;
      const xOff = (Math.random() - 0.5) * canvas.width * 0.9;
      motes.push({
        x: cx + xOff,
        y: cy + side * (Math.random() * 8),
        vx: (Math.random() - 0.5) * 0.3,
        vy: -(0.2 + Math.random() * 0.5),
        life: 0,
        maxLife: 90 + Math.random() * 120,
        size: 0.8 + Math.random() * 1.6,
        opacity: 0,
        hue: 38 + Math.random() * 14,
      });
    };

    let t = 0;

    function draw() {
      t++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const dimMult = isTyping ? 0.15 : 1;
      const currentFog = mistFog;

      // ─ 横ラインのグロー ─
      const lineAlpha = 0.12 * dimMult * (1 - currentFog * 0.8);
      const lineGrd = ctx.createLinearGradient(0, cy, canvas.width, cy);
      lineGrd.addColorStop(0, "rgba(201,168,76,0)");
      lineGrd.addColorStop(0.2, `rgba(201,168,76,${lineAlpha})`);
      lineGrd.addColorStop(0.5, `rgba(255,210,80,${lineAlpha * 1.8})`);
      lineGrd.addColorStop(0.8, `rgba(201,168,76,${lineAlpha})`);
      lineGrd.addColorStop(1, "rgba(201,168,76,0)");
      ctx.beginPath();
      ctx.moveTo(0, cy);
      ctx.lineTo(canvas.width, cy);
      ctx.strokeStyle = lineGrd;
      ctx.lineWidth = 1;
      ctx.stroke();

      // ─ 焦りの霧（ミス増加で濃くなる） ─
      if (currentFog > 0) {
        fogMotes.forEach((f) => {
          f.x += f.vx;
          f.y += f.vy;
          f.phase += 0.008;
          if (f.x < -f.size) f.x = canvas.width + f.size;
          if (f.x > canvas.width + f.size) f.x = -f.size;
          if (f.y < -f.size) f.y = canvas.height + f.size;
          if (f.y > canvas.height + f.size) f.y = -f.size;

          const fogAlpha = f.opacity * currentFog * (0.5 + Math.sin(f.phase) * 0.5);
          const fogGrd = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.size);
          // ミス多いほど赤みがかった霧に
          const r = Math.floor(20 + currentFog * 60);
          const g = Math.floor(10 + currentFog * 5);
          const b = Math.floor(10 + currentFog * 5);
          fogGrd.addColorStop(0, `rgba(${r},${g},${b},${fogAlpha * 0.8})`);
          fogGrd.addColorStop(0.5, `rgba(${r},${g},${b},${fogAlpha * 0.4})`);
          fogGrd.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = fogGrd;
          ctx.beginPath();
          ctx.arc(f.x, f.y, f.size, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      // ─ 金の粒子 ─
      const spawnChance = 0.25 * dimMult * (1 - currentFog * 0.6);
      if (Math.random() < spawnChance && motes.length < 30) spawnMote();

      for (let i = motes.length - 1; i >= 0; i--) {
        const m = motes[i];
        m.life++;
        m.x += m.vx;
        m.y += m.vy;
        const prog = m.life / m.maxLife;
        m.opacity = prog < 0.25
          ? (prog / 0.25) * 0.7 * dimMult * (1 - currentFog * 0.7)
          : (1 - (prog - 0.25) / 0.75) * 0.7 * dimMult * (1 - currentFog * 0.7);
        if (m.life >= m.maxLife) { motes.splice(i, 1); continue; }
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${m.hue}, 80%, 65%, ${m.opacity})`;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mistFog]);

  const glowOpacity =
    glowPhase === "enter" ? 0.5 :
    glowPhase === "pulse" ? 1 :
    0.2;

  return (
    <div className="relative w-full flex flex-col items-center">

      {/* ── 金のグロー Canvas ── */}
      <canvas
        ref={canvasRef}
        className="absolute pointer-events-none"
        style={{
          width: "100%",
          height: "60px",
          top: "50%",
          left: 0,
          transform: "translateY(-50%)",
          opacity: isTyping ? 0.15 : glowOpacity,
          transition: "opacity 1.4s ease",
        }}
      />

      {/* ── 脈打つグローリング ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "200px",
          height: "1px",
          background: isTyping
            ? "rgba(201,168,76,0.05)"
            : `rgba(201,168,76,${0.25 * (1 - mistFog * 0.8)})`,
          boxShadow: isTyping
            ? "none"
            : mistFog > 0.5
            ? "none"
            : "0 0 12px 2px rgba(201,168,76,0.15), 0 0 24px 4px rgba(255,200,60,0.08)",
          transition: "all 1.4s ease",
          animation: !isTyping && glowPhase === "pulse" && mistFog < 0.5
            ? "goldPulse 3.2s ease-in-out infinite"
            : "none",
        }}
      />

      {/* ── 焦りの霧オーバーレイ（ミス増加で濃くなる） ── */}
      {mistFog > 0 && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            borderRadius: "8px",
            background: `radial-gradient(ellipse at 50% 50%, rgba(15,5,5,${mistFog * 0.7}) 0%, transparent 80%)`,
            transition: "background 1.2s ease",
            backdropFilter: mistFog > 0.4 ? `blur(${mistFog * 3}px)` : "none",
          }}
        />
      )}

      {/* ── ヒントテキスト（文字が一つずつ現れる） ── */}
      <div
        className="relative z-10 text-center mb-5"
        style={{
          opacity: isTyping ? 0 : glowOpacity * hintOpacity,
          transition: "opacity 1.2s ease",
          minHeight: "24px",
          filter: textBlur > 0 ? `blur(${textBlur}px)` : "none",
        }}
      >
        {/* 上の光の線 */}
        <div
          className="mx-auto mb-3"
          style={{
            width: visibleChars > 0 ? "24px" : "0px",
            height: "1px",
            background: `rgba(201,168,76,${0.3 * (1 - mistFog * 0.7)})`,
            transition: "width 1s ease, background 1.2s ease",
          }}
        />

        {/* 文字 */}
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "11px",
            letterSpacing: "0.35em",
            fontStyle: "italic",
            color: `rgba(201,168,76,${0.55 * (1 - mistFog * 0.6)})`,
            textShadow: mistFog > 0.5
              ? "none"
              : `0 0 16px rgba(255,200,60,${0.3 * (1 - mistFog)}), 0 0 32px rgba(201,168,76,${0.15 * (1 - mistFog)})`,
            whiteSpace: "nowrap",
            animation: glowPhase === "pulse" && mistFog < 0.3
              ? "hintBreath 4s ease-in-out infinite"
              : "none",
            transition: "color 1.2s ease, text-shadow 1.2s ease",
          }}
        >
          {HINT_TEXT.split("").map((char, i) => (
            <span
              key={i}
              style={{
                opacity: i < visibleChars ? 1 : 0,
                transform: i < visibleChars ? "translateY(0)" : "translateY(4px)",
                display: "inline-block",
                transition: `opacity 0.6s ease, transform 0.6s ease`,
                // ミスが多いほど文字が浮遊してぼける
                filter: mistFog > 0.3
                  ? `blur(${(mistFog - 0.3) * 4 * (Math.sin(i * 1.3) * 0.5 + 1)}px)`
                  : "none",
                transform:
                  i < visibleChars
                    ? mistFog > 0.4
                      ? `translateY(${Math.sin(i * 2.1 + Date.now() * 0.001) * mistFog * 3}px)`
                      : "translateY(0)"
                    : "translateY(4px)",
              }}
            >
              {char}
            </span>
          ))}
        </p>

        {/* ミス多数時の「霧に包まれた」示唆テキスト */}
        {mistCount >= 3 && (
          <div
            className="mt-2"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "9px",
              letterSpacing: "0.3em",
              fontStyle: "italic",
              color: `rgba(180,60,60,${Math.min((mistCount - 2) * 0.12, 0.45)})`,
              transition: "color 1s ease",
            }}
          >
            焦りが、真実を隠す
          </div>
        )}

        {/* 下の光の点 */}
        <div className="flex items-center justify-center gap-2 mt-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: "2px",
                height: "2px",
                borderRadius: "50%",
                background: `rgba(201,168,76,${0.3 * (1 - mistFog * 0.8)})`,
                opacity: visibleChars >= HINT_TEXT.length ? 1 : 0,
                transition: `opacity 0.8s ease ${i * 0.3}s, background 1.2s ease`,
                animation: glowPhase === "pulse" && mistFog < 0.4
                  ? `dotPulse ${2 + i * 0.4}s ease-in-out infinite`
                  : "none",
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes goldPulse {
          0%, 100% {
            box-shadow: 0 0 12px 2px rgba(201,168,76,0.12), 0 0 24px 4px rgba(255,200,60,0.06);
            opacity: 0.7;
          }
          50% {
            box-shadow: 0 0 20px 4px rgba(201,168,76,0.28), 0 0 40px 8px rgba(255,200,60,0.14);
            opacity: 1;
          }
        }
        @keyframes hintBreath {
          0%, 100% { opacity: 0.45; text-shadow: 0 0 14px rgba(255,200,60,0.2), 0 0 28px rgba(201,168,76,0.1); }
          50%       { opacity: 0.75; text-shadow: 0 0 20px rgba(255,200,60,0.4), 0 0 40px rgba(201,168,76,0.2); }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50%       { opacity: 0.7;  transform: scale(1.5); }
        }
      `}</style>
    </div>
  );
}
